import { ChevronDown, Columns2, Copy, Pin, PinOff, X } from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Kbd,
  cn,
  useAltHeld,
} from '@/components/ui';
import { useObserverRegistry } from '@/components/observer/observerContext';
import { VIEW_TAB, type PaneId } from './types';
import { useWorkspace } from './workspaceContext';

/*
  TabStrip — полоса вкладок одного окна (уровень навигации 3, docs/design-patterns-v3.md §3).
  Верхний край холста: muted-фон, активная вкладка сливается с содержимым. В основном окне
  первая вкладка — вид (куда привело меню); её нельзя закрыть, закрепить или унести.

  Жесты: клик — открыть; средняя кнопка — закрыть; перетаскивание — порядок или в другое окно;
  правая кнопка — меню вкладки. Удержание Alt проявляет номера вкладок (Alt+1…9).
  Не влезают — полоса прокручивается, а справа появляется «все вкладки». Упавшая вкладка (её поймала своя
  граница, R42) носит красную точку на ярлыке — видно и тогда, когда она на фоне.

  Ярлык — цель помощника (spec-3): адрес tab:<окно>:<путь>, уровень 2. Без помощника ничего не делает.
*/

interface TabStripProps {
  pane: PaneId;
  /** Окно в фокусе — его активная вкладка отмечена полоской сверху (когда окон два). */
  focused: boolean;
  split: boolean;
  /** Упавшие вкладки (R42): красная точка на ярлыке и «— сбой» в имени для читалки. */
  crashed?: ReadonlySet<string>;
  trailing?: ReactNode;
}

interface MenuAt {
  id: string;
  x: number;
  y: number;
}

export function TabStrip({ pane, focused, split, crashed, trailing }: TabStripProps) {
  const ws = useWorkspace();
  const { state, resolve, dragging } = ws;
  const registry = useObserverRegistry();
  const altHeld = useAltHeld();
  const p = pane === 'main' ? state.main : state.side;
  const listRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef(new Map<string, HTMLDivElement>());
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [overflow, setOverflow] = useState(false);
  const [menu, setMenu] = useState<MenuAt | null>(null);

  const ids = p ? (pane === 'main' ? [VIEW_TAB, ...p.tabs] : p.tabs) : [];
  const objectIds = p?.tabs ?? [];

  // Переполнение: полоса шире окна — показываем «все вкладки». Наблюдатель срабатывает и сразу.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setOverflow(el.scrollWidth > el.clientWidth + 1));
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    return () => ro.disconnect();
  }, [ids.length]);

  // Активная вкладка всегда в видимой части полосы.
  const activeId = p?.active;
  useEffect(() => {
    if (activeId) tabRefs.current.get(activeId)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeId]);

  if (!p) return null;

  const pathOf = (id: string) => (id === VIEW_TAB ? state.view : (state.tabs[id]?.path ?? state.view));

  /** Позиция вставки среди объектов окна по горизонтали указателя. */
  const indexAt = (clientX: number) => {
    let i = 0;
    for (const id of objectIds) {
      const el = tabRefs.current.get(id);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientX < r.left + r.width / 2) return i;
      i += 1;
    }
    return objectIds.length;
  };

  const onDragOver = (e: DragEvent) => {
    if (!dragging) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropIndex(indexAt(e.clientX));
  };

  const onDrop = (e: DragEvent) => {
    if (!dragging) return;
    e.preventDefault();
    ws.move(dragging.id, pane, indexAt(e.clientX));
    setDropIndex(null);
    ws.setDragging(null);
  };

  const menuTab = menu ? state.tabs[menu.id] : null;

  return (
    <div
      data-slot="tab-strip"
      className="relative flex h-11 shrink-0 items-end gap-1 bg-tabs pl-2 shadow-hairline-b"
      onDragOver={onDragOver}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropIndex(null);
      }}
      onDrop={onDrop}
    >
      <div
        ref={listRef}
        role="tablist"
        aria-label={pane === 'main' ? 'Вкладки основного окна' : 'Вкладки второго окна'}
        className="scrollbar-none flex min-w-0 items-end gap-1 overflow-x-auto"
      >
        {ids.map((id, n) => {
          const isView = id === VIEW_TAB;
          const tab = isView ? null : state.tabs[id];
          const path = pathOf(id);
          const r = resolve(path);
          const title = isView ? r.title : (tab?.title ?? r.title);
          const active = p.active === id;
          const pinned = Boolean(tab?.pinned);
          const closable = !isView && !pinned;
          const objIndex = isView ? -1 : objectIds.indexOf(id);
          const broken = crashed?.has(id) ?? false;
          const Icon = r.icon;
          return (
            <div
              key={id}
              ref={(el) => {
                if (el) tabRefs.current.set(id, el);
                else tabRefs.current.delete(id);
              }}
              role="presentation"
              className="group/tab relative flex shrink-0 items-end"
            >
              {dropIndex !== null && objIndex === dropIndex && (
                <span className="absolute top-2 bottom-1 -left-1 w-0.5 rounded-full bg-brand" aria-hidden />
              )}
              <button
                ref={(el) => {
                  if (!registry) return;
                  const anchor = `tab:${pane}:${path}`;
                  if (el) registry.registerAnchor(anchor, el, { role: 'source', label: title, level: 2 });
                  return () => registry.unregisterAnchor(anchor, el ?? undefined);
                }}
                type="button"
                role="tab"
                aria-selected={active}
                aria-label={broken ? `${title}${pinned ? ', закреплена' : ''} — сбой` : undefined}
                title={r.trail ?? title}
                draggable={!isView}
                onClick={() => ws.activate(pane, id)}
                onAuxClick={(e) => {
                  if (e.button === 1 && closable) {
                    e.preventDefault();
                    ws.close(pane, id);
                  }
                }}
                onContextMenu={(e) => {
                  if (isView) return;
                  e.preventDefault();
                  const box = e.currentTarget.closest('[data-slot="tab-strip"]')!.getBoundingClientRect();
                  setMenu({ id, x: e.clientX - box.left, y: e.clientY - box.top });
                }}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', window.location.origin + path);
                  ws.setDragging({ id, from: pane });
                }}
                onDragEnd={() => {
                  ws.setDragging(null);
                  setDropIndex(null);
                }}
                className={cn(
                  'relative flex h-8 max-w-56 items-center gap-1.5 rounded-t-sm border border-b-0 pl-3 text-xs select-none',
                  closable ? 'pr-7' : 'pr-3',
                  'transition-colors duration-fast focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-inset',
                  active
                    ? 'border-line bg-canvas text-fg'
                    : 'border-transparent text-fg-muted hover:bg-hover hover:text-fg',
                  dragging?.id === id && 'opacity-50',
                )}
              >
                {active && focused && split && (
                  <span className="absolute inset-x-2 top-0 h-0.5 rounded-full bg-brand" aria-hidden />
                )}
                <Icon className="size-3.5 shrink-0 text-fg-subtle" />
                <span className={cn('truncate', isView ? 'font-semibold' : 'font-medium')}>{title}</span>
                {broken && <TabCrashDot />}
                {pinned && <Pin className="size-3 shrink-0 text-fg-subtle" aria-label="Закреплена" />}
                {altHeld && n < 9 && <Kbd className="animate-fade-in">{n + 1}</Kbd>}
              </button>
              {closable && (
                <button
                  type="button"
                  aria-label={`Закрыть ${title}`}
                  onClick={() => ws.close(pane, id)}
                  className={cn(
                    'absolute top-1/2 right-1.5 grid size-4 -translate-y-1/2 place-items-center rounded-xs text-fg-subtle',
                    'opacity-0 transition-opacity duration-fast group-hover/tab:opacity-100 hover:bg-active hover:text-fg',
                    'focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                    active && 'opacity-100',
                  )}
                >
                  <X className="size-3" />
                </button>
              )}
            </div>
          );
        })}
        {dropIndex !== null && dropIndex === objectIds.length && (
          <span className="mb-1 h-6 w-0.5 shrink-0 rounded-full bg-brand" aria-hidden />
        )}
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-1 self-center pr-2">
        {overflow && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex h-7 items-center gap-1 rounded-sm px-2 text-xs text-fg-muted transition-colors duration-fast hover:bg-hover hover:text-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                Все вкладки · {ids.length}
                <ChevronDown className="size-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-96 overflow-y-auto">
              {ids.map((id) => {
                const r = resolve(pathOf(id));
                const Icon = r.icon;
                return (
                  <DropdownMenuItem key={id} leftIcon={<Icon />} onSelect={() => ws.activate(pane, id)}>
                    {id === VIEW_TAB ? r.title : (state.tabs[id]?.title ?? r.title)}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {trailing}
      </div>

      {/* Меню вкладки по правой кнопке: невидимый якорь там, где кликнули. */}
      <DropdownMenu open={Boolean(menu)} onOpenChange={(open) => !open && setMenu(null)}>
        <DropdownMenuTrigger asChild>
          <span
            className="pointer-events-none absolute size-px"
            style={menu ? { left: menu.x, top: menu.y } : undefined}
            aria-hidden
          />
        </DropdownMenuTrigger>
        {menuTab && (
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              leftIcon={menuTab.pinned ? <PinOff /> : <Pin />}
              onSelect={() => ws.pin(menuTab.id, !menuTab.pinned)}
            >
              {menuTab.pinned ? 'Открепить' : 'Закрепить'}
            </DropdownMenuItem>
            <DropdownMenuItem
              leftIcon={<Columns2 />}
              shortcut="Alt+\"
              onSelect={() => ws.move(menuTab.id, pane === 'main' ? 'side' : 'main')}
            >
              {pane === 'main' ? 'Открыть рядом' : 'Вернуть в основное окно'}
            </DropdownMenuItem>
            <DropdownMenuItem
              leftIcon={<Copy />}
              onSelect={() => void navigator.clipboard?.writeText(window.location.origin + menuTab.path)}
            >
              Скопировать адрес
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {!menuTab.pinned && (
              <DropdownMenuItem leftIcon={<X />} shortcut="Alt+W" onSelect={() => ws.close(pane, menuTab.id)}>
                Закрыть
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => ws.closeOthers(pane, menuTab.id)}>
              Закрыть остальные
            </DropdownMenuItem>
          </DropdownMenuContent>
        )}
      </DropdownMenu>
    </div>
  );
}

/** Точка сбоя на ярлыке вкладки (R42): вкладка упала, остальные работают. Для читалки — «— сбой» в имени. */
export function TabCrashDot() {
  return <span aria-hidden data-slot="tab-crash-dot" className="size-1.5 shrink-0 rounded-full bg-danger" />;
}
